<template>
  <div class="google-account-edit-container d-flex flex-column w-100" :class="{ 'is-add-margin': isAdd }">
    <!-- タイトルバー（件数表示付き） -->
    <ListTitleBar ref="listTitleBarRef" titleCaption="設定対象の端末" :rowCount="rowCount" :rowCountArea="isAdd" />
    <!-- 端末一覧リスト -->
    <ListCtrl ref="listCtrlRef" class="flex-grow-1" :columns="COLUMNS" :selectType="1" :getCellTextFunc="getCellText"
      :sortDeleteIndex="false" :style="listStyle" />
  </div>

  <div class="setting-detail-area">
    <div
      class="main-setting-table main-category-font scv-diagnosisdetails-title googleaccountagentsettingedit-main-setting-table">
      Googleアカウントでのログイン設定
    </div>

    <div class="setting-detail-area-inner">

      <div class="default-text-font" style="height: 36px; margin-bottom: 16px;">
        Googleアカウントでログインして端末を利用することを任意とするか、必須とするかを設定してください。<br />
        必須を設定する場合は、ログインに使用するGoogleアカウントを指定することもできます。
      </div>

      <!-- 設定インプットエリア -->
      <div class="form-container">
        <!-- 1. Googleアカウントによるログイン（ラジオボタン） -->
        <div class="form-group">
          <!-- 任意（ログイン不要） -->
          <div class="d-flex flex-row align-items-center" style="height: 32px;">
            <input type="radio" v-model="editSetting.authenticationType"
              :value="workAccountSetupConfigDefaultFalseValue" style="margin-right: 5px; cursor: pointer;"
              id="radio-google-auth-optional">
            <label for="radio-google-auth-optional" style="cursor: pointer; font-size: 13px;">任意</label>
          </div>

          <!-- 必須（アカウント指定ログインが必要） -->
          <div class="d-flex flex-row align-items-start" style="height: 100px;">
            <div class="d-flex flex-row align-items-center" style="height: 32px; margin-top: 2px;">
              <input type="radio" v-model="editSetting.authenticationType"
                :value="workAccountSetupConfigDefaultTrueValue" style="margin-right: 5px; cursor: pointer;"
                id="radio-google-auth-required">
              <label for="radio-google-auth-required"
                style="cursor: pointer; font-size: 13px; margin-right: 4px; white-space: nowrap;">必須</label>
            </div>

            <!-- ValidatedInputによるメールアドレス入力欄 -->
            <div class="d-flex flex-row align-items-center" style="height: 32px; margin-top: 2px; margin-left: 16px;">
              <span style="white-space: nowrap;">Googleアカウント</span>
              <InformationManual :arMessage="[getInfoManualInput()]" />：
            </div>

            <ValidatedInput style="width: 651px;" v-model="editSetting.requiredAccountEmail" label=""
              inputType="mail-address" :inputWidth="651" :labelWidth="0" :labelLayout="VILabelLayout.Horizontal"
              :disabled="editSetting.authenticationType !== workAccountSetupConfigDefaultTrueValue" :maxLength="256"
              note="※Googleアカウントを指定しない場合は、組織で管理されている任意のGoogleアカウントでログインできるようになります。">
              <template #input-under v-if="errorGoogleAccount">
                <div style="height: 20px;">
                  <span class="scv-form-validation-error error">{{ errorGoogleAccount }}</span>
                </div>
              </template>
            </ValidatedInput>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, computed } from 'vue';
import ListTitleBar from '@/components/ListTitleBar.vue';
import { ListCtrl } from 'lib-vue-v2/components';
import { SearchColumnData, SearchColumnType } from '@/models/SearchColumnData';
import { AndroidSimpleAsset } from '@/views/Mobile/Android/GoogleAccountSetting/model/AndroidAsset';
import { AMAPI_WorkAccountSetupConfig } from '@/models/Mobile/Android/AndroidManagementV1Schema';
import * as schemaConstants from '@/models/Mobile/Android/AndroidManagementV1SchemaConstants';
import InformationManual from '@/components/InformationManual.vue';
import { MessageText } from '@/models/Message/MessageText';
import { ValidatedInput, VILabelLayout } from "lib-vue-v2/components";

// --- Props定義 ---
interface Props {
  /** 編集対象として選択された端末のリスト */
  mode: number;// 0:add/1:edit
  targetAssets: Array<AndroidSimpleAsset>;
  settingData?: AMAPI_WorkAccountSetupConfig;
  listHeight?: number | string;
}

const props = withDefaults(defineProps<Props>(), {
  targetAssets: () => [],
  settingData: () => ({
    authenticationType: schemaConstants.AMAPI_AuthenticationType_Enums.authentication_type_not_enforces,
    requiredAccountEmail: ''
  })
});

const emit = defineEmits<{
  (e: 'update:settingData', value: AMAPI_WorkAccountSetupConfig): void;
}>();

// --- 定数定義 ---
const workAccountSetupConfigDefaultTrueValue = schemaConstants.AMAPI_AuthenticationType_Enums.googleAuthEnticated;
const workAccountSetupConfigDefaultFalseValue = schemaConstants.AMAPI_AuthenticationType_Enums.authentication_type_not_enforces;
const GOOGLE_ACCOUNT_MIN_LENGTH = 1;
const GOOGLE_ACCOUNT_MAX_LENGTH = 256;

// --- Refs ---
const listTitleBarRef = ref<InstanceType<typeof ListTitleBar> | null>(null);
const listCtrlRef = ref<InstanceType<typeof ListCtrl> | null>(null);
const rowCount = ref(0);

// エラーバインド用の変数
const errorGoogleAccount = ref('');

interface LocalEditSetting {
  authenticationType: string;
  requiredAccountEmail: string;
}

// --- 入力データ（API定義のAMAPI_WorkAccountSetupConfigを使用） ---
const editSetting = ref<LocalEditSetting>({
  authenticationType: workAccountSetupConfigDefaultFalseValue,
  requiredAccountEmail: ''
});

// --- カラム定義 ---
const COLUMNS: Array<SearchColumnData> = [
  { columnID: 0, dataType: SearchColumnType.number, text: "端末機No." },
  { columnID: 1, dataType: SearchColumnType.string, text: "端末機名" },
  { columnID: 2, dataType: SearchColumnType.string, text: "コンピューター名" },
  { columnID: 3, dataType: SearchColumnType.string, text: "部署名" },
];

// --- Methods ---
const listStyle = computed(() => {
  if (!props.listHeight) return {}; // 指定がない場合はスタイル追加なし

  const heightValue = typeof props.listHeight === 'number'
    ? `${props.listHeight}px`
    : props.listHeight;

  return {
    height: heightValue,
    flexGrow: 0, // 高さを固定する場合は flex-grow-1 の効果を打ち消す
    flexShrink: 0
  };
});

const isAdd = computed(() => {
  return props.mode == 0;
});

const getInfoManualInput = (): string => {
  /* eslint-disable no-irregular-whitespace */
  return `・組織で管理されているGoogleアカウントを指定してください。<br/>
        　管理されていないGoogleアカウントを指定した場合は、<br/>
        　「Googleアカウントでのログイン設定」は適用されません。<br/>
        <br/>
        ・指定したGoogleアカウントで92日間ログインが行われなかった場合は、<br/>
        　端末がリセット（すべてのデータの消去）されます。`;
  /* eslint-enable no-irregular-whitespace */
}

const updateRowCount = async () => {
  await nextTick();
  if (listCtrlRef.value) {
    rowCount.value = listCtrlRef.value.getDisplayListIndex().length;
  }
};

const updateList = () => {
  if (listCtrlRef.value) {
    listCtrlRef.value.updateList(props.targetAssets.length);
  }
  updateRowCount();
};

const getCellText = (columnId: number, index: number): string => {
  const asset = props.targetAssets[index];
  if (!asset) return "";
  switch (columnId) {
    case 0: return String(asset.agentNo);
    case 1: return asset.agentName || "";
    case 2: return asset.computerName || "";
    case 3: return asset.divisionName || "";
  }
  return "";
};

const sanitizeAndSetData = (source: AMAPI_WorkAccountSetupConfig) => {
  let authType = source.authenticationType;
  if (!schemaConstants.isAMAPI_AuthenticationType_Enums(authType)) {
    authType = workAccountSetupConfigDefaultFalseValue;
  }

  const newAuthType = authType ?? workAccountSetupConfigDefaultFalseValue;
  const newEmail = source.requiredAccountEmail ?? '';

  if (
    editSetting.value.authenticationType === newAuthType &&
    editSetting.value.requiredAccountEmail === newEmail
  ) {
    return;
  }

  editSetting.value = {
    authenticationType: newAuthType,
    requiredAccountEmail: newEmail
  };
};

// --- 全体のバリデーション関数（親から呼ばれる） ---
const validateCheck = (): boolean => {
  // 毎回チェック前にエラーメッセージをクリア
  errorGoogleAccount.value = "";

  // 「必須」以外の場合はメールアドレスのチェック不要（無条件でOK）
  if (editSetting.value.authenticationType !== workAccountSetupConfigDefaultTrueValue) {
    return true;
  }

  // メールアドレスのチェック
  const emailInput = editSetting.value.requiredAccountEmail.trim();
  editSetting.value.requiredAccountEmail = emailInput;
  return validateEmail(emailInput);
};

// --- メールアドレス専用のバリデーション関数 ---
const validateEmail = (emailInput: string): boolean => {
  // メールアドレスの「空（未入力）」はOK
  if (!emailInput) return true;

  // 1. 長さチェック
  if (emailInput.length < GOOGLE_ACCOUNT_MIN_LENGTH ||
    GOOGLE_ACCOUNT_MAX_LENGTH < emailInput.length) {
    errorGoogleAccount.value = MessageText.CommonInputFailedRength.format(
      GOOGLE_ACCOUNT_MIN_LENGTH.toString(),
      GOOGLE_ACCOUNT_MAX_LENGTH.toString()
    );
    return false;
  }

  // 2. 制御文字チェック
  if (emailInput.includeControlCode()) {
    errorGoogleAccount.value = MessageText.CommonInputFailedControlCode;
    return false;
  }

  // 3. メールアドレスフォーマットチェック
  if (!emailInput.isValidMailAddress2()) {
    errorGoogleAccount.value = MessageText.CommonInputFailedMailAddress;
    return false;
  }

  return true;
};

// --- Watchers ---
watch(() => props.targetAssets, async () => {
  await nextTick();
  updateList();
}, { deep: true, immediate: true });

watch(() => props.settingData, (newVal) => {
  if (newVal) {
    sanitizeAndSetData(newVal);
  }
}, { deep: true, immediate: true });

watch(editSetting, (newVal) => {
  emit('update:settingData', { ...newVal });
}, { deep: true });

onMounted(async () => {
  await nextTick();
  updateList();
});

defineExpose({
  validateCheck
});
</script>

<style lang="scss" scoped>
.google-account-edit-container {
  display: flex;
  flex-direction: column;

  &.is-add-margin {
    margin-top: 16px;
  }
}

.scv-diagnosisdetails-title {
  margin-top: 20px;
  margin-bottom: 10px;
  padding-left: 0px;
  font-size: 14px;
  min-height: 30px;
}

.setting-detail-area {
  margin-top: 16px;
}

.googleaccountagentsettingedit-main-setting-table {
  height: 44px;
  margin: 0px 0px 0px 0px !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.setting-detail-area-inner {
  margin: 16px 16px 0px 16px;
}
</style>
